import React, { useState, useEffect, useCallback, useMemo } from 'react';
import client from '@/lib/feathers';
import { Button, Message, Table } from '@alifd/next';
import {
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Select,
  Typography,
} from 'antd';
import { Box } from '@mui/material';
import styled from '@emotion/styled';
import PageHeader from '@/components/PageHeader';
import axios from 'axios';
import Swal from 'sweetalert2';
import ReactDragListView from 'react-drag-listview';
import type { ChargingPile } from '@/types/database/chargingPile/chargingPile';
import type { ChargingPileGroup } from '@/types/database/chargingPile/chargingPileGroup';
import CircuitTag from '../components/groupTags/CircuitTag';
import ViewTag from '../components/groupTags/ViewTag';

const { Item } = Form;
const { Option } = Select;

interface TableData extends Omit<ChargingPileGroup, 'children'> {
  children: ChargingPile[];
}

const TableContainer = styled.div`
  padding: 20px;
  background-color: #fff;
  border-radius: 4px;
`;

const ChargingPileGroupManagement = () => {
  const [groupData, setGroupData] = useState<ChargingPileGroup[]>([]);
  const [pileData, setPileData] = useState<ChargingPile[]>([]);
  const [dataSource, setDataSource] = useState<TableData[]>([]);
  const [visible, setVisible] = useState(false);
  const [currentGroup, setCurrentGroup] = useState<TableData | null>(null);
  const [triggerRefresh, setTriggerRefresh] = useState({});
  const [isCircuitGroup, setIsCircuitGroup] = useState(false);

  const [form] = Form.useForm<{ name: string; children: string[] }>();

  const resetModal = useCallback(() => {
    setVisible(false);
    setCurrentGroup(null);
    form.resetFields();
    setIsCircuitGroup(false);
  }, [form]);

  const handleSubmit = useCallback(
    async (values: { name: string; children: string[] }) => {
      const { children } = values;
      // 如果有選擇群組，則更新群組，否則新增群組
      const newData = {
        ...values,
        children: children.sort((a, b) => a.localeCompare(b)),
        chargingArray: [],
        waitChargingQueue: [],
        readyToCharging: [],
        activated: true,
      };
      try {
        if (currentGroup) {
          await client
            .service('chargingPileGroup')
            .patch(currentGroup._id, newData);
        } else {
          newData['order'] = groupData.length;
          await client.service('chargingPileGroup').create(newData);
        }
        Message.success(currentGroup ? '更新成功' : '新增成功');
        resetModal();
        setTriggerRefresh({});
      } catch (error) {
        console.error(error);
        Message.error('更新失敗');
      }
    },
    [currentGroup, groupData.length, resetModal]
  );

  const handleDelete = useCallback(async (record: TableData) => {
    const result = await Swal.fire({
      title: '確定刪除此群組？',
      text: '刪除後將無法恢復',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: '確定',
      cancelButtonText: '取消',
    });
    if (result.value) {
      try {
        await client.service('chargingPileGroup').remove(record._id);
        Message.success('刪除成功');
        setTriggerRefresh({});
      } catch (error) {
        console.error(error);
        Message.error('刪除失敗');
      }
    }
  }, []);

  const handleChangeActivateState = useCallback(async (record: TableData) => {
    try {
      await client.service('chargingPileGroup').patch(record._id, {
        activated: !record.activated,
      });
      Message.success('更新成功');
      setTriggerRefresh({});
    } catch (err) {
      console.error(err);
    }
  }, []);

  const columns = useMemo(
    () => [
      {
        title: '排序',
        dataIndex: 'order',
        cell: (val: number) => val + 1,
      },
      {
        title: '群組名稱',
        dataIndex: 'name',
      },
      {
        title: '群組類型',
        dataIndex: 'type',
        cell: (type: string) => {
          switch (type) {
            case 'circuit':
              return '電路群組';
            case 'view':
              return '顯示群組';
            default:
              return '未知';
          }
        },
      },
      {
        title: '最大同時充電數',
        dataIndex: 'maxChargingNumber',
        cell: (maxChargingNumber: number | undefined) =>
          maxChargingNumber || '無',
      },
      {
        title: '群組成員',
        dataIndex: 'children',
        cell: (children: ChargingPile[]) =>
          children
            .sort((a, b) => a.chargingPileID.localeCompare(b.chargingPileID))
            .map((child) => child.chargingPileID)
            .join(', '),
      },
      {
        title: '啟用',
        dataIndex: 'activated',
        cell: (activated: boolean) => (activated ? '是' : '否'),
      },
      {
        title: '操作',
        cell: (_val: undefined, _index: number, record: TableData) => (
          <Box
            sx={{
              display: 'flex',
              gap: '10px',
            }}
          >
            <Button
              onClick={() => {
                setCurrentGroup(record);
                setVisible(true);
                if (record.type === 'circuit') {
                  setIsCircuitGroup(true);
                }
                form.setFieldsValue({
                  ...record,
                  children: record.children.map((child) => child._id),
                });
              }}
            >
              編輯
            </Button>
            <Button onClick={() => handleChangeActivateState(record)}>
              {record.activated ? '停用' : '啟用'}
            </Button>
            <Button onClick={() => handleDelete(record)} warning type="primary">
              刪除
            </Button>
          </Box>
        ),
      },
    ],
    [form, handleDelete, handleChangeActivateState]
  );

  useEffect(() => {
    const getData = async () => {
      try {
        const pileRes: { data: ChargingPile[] } = await axios.post(
          '/ezcon/api/postMongoAggregate',
          {
            service: 'chargingPile',
            aggregate: [
              {
                $lookup: {
                  from: 'baDevice',
                  localField: 'chargingPileID',
                  foreignField: 'name',
                  as: 'name',
                },
              },
              {
                $addFields: {
                  name: { $arrayElemAt: ['$name.name', 0] },
                },
              },
            ],
          },
          {
            headers: {
              authorization: localStorage.getItem('feathers-jwt'),
            },
          }
        );
        setPileData(pileRes.data);
        const groupRes: { data: ChargingPileGroup[] } = await client
          .service('chargingPileGroup')
          .find({});
        let newGroupData = groupRes.data.sort((a, b) => {
          if (typeof a.order === 'number' && typeof b.order === 'number') {
            return a.order - b.order;
          } else if (typeof a.order === 'number') {
            return -1;
          }
          return 1;
        });
        newGroupData = newGroupData.map((group, index) => ({
          ...group,
          order: index,
        }));
        setGroupData(newGroupData);
      } catch (error) {
        console.error(error);
      }
    };
    getData();
  }, [triggerRefresh]);

  useEffect(() => {
    if (pileData.length > 0) {
      setDataSource(
        groupData.map((group) => {
          const children: ChargingPile[] = [];
          group.children.forEach((child) => {
            const pile = pileData.find((p) => p._id === child);
            if (pile) {
              children.push(pile);
            }
          });
          return {
            ...group,
            children,
          };
        })
      );
    }
  }, [groupData, pileData]);

  const handleDragEnd = useCallback(
    async (from: number, to: number) => {
      const newData = [...groupData];
      const movingItem = newData.splice(from, 1);
      newData.splice(to, 0, ...movingItem);
      try {
        const promises = newData.map((group, index) => {
          if (group.order !== index) {
            return client.service('chargingPileGroup').patch(group._id, {
              order: index,
            });
          }
        });

        await Promise.all(promises);

        newData.forEach((group, index) => {
          group.order = index;
        });
        setGroupData(newData);

        Message.success('更新排序成功');
      } catch (err) {
        console.error(err);
      }
    },
    [groupData]
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
      }}
    >
      <PageHeader
        title="充電樁群組"
        breadcrumbs={[
          {
            name: '汽車充電樁',
          },
          {
            name: '充電樁群組',
          },
        ]}
      />
      <Button
        style={{
          width: 'fit-content',
        }}
        onClick={() => setVisible(true)}
      >
        新增群組
      </Button>
      <Typography.Text type="secondary">
        *拖曳群組可調整群組排序
      </Typography.Text>
      <TableContainer>
        <ReactDragListView onDragEnd={handleDragEnd}>
          <Table {...{ dataSource, columns, pagination: 'false' }} />
        </ReactDragListView>
      </TableContainer>
      <Modal
        title={currentGroup ? '編輯群組' : '新增群組'}
        open={visible}
        onOk={form.submit}
        onCancel={resetModal}
        okText="儲存"
        cancelText="取消"
        forceRender
      >
        <Form
          form={form}
          initialValues={{
            name: '',
            children: [],
          }}
          onFinish={handleSubmit}
          onFieldsChange={(changedFields) => {
            const { name, value } = changedFields[0];
            if (Array.isArray(name) && name.includes('type')) {
              if (value === 'circuit') {
                setIsCircuitGroup(true);
              } else {
                setIsCircuitGroup(false);
              }
            }
          }}
          labelAlign="left"
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 16 }}
        >
          <Item
            label="群組名稱"
            name="name"
            rules={[{ required: true, message: '請輸入名稱' }]}
          >
            <Input />
          </Item>
          <Item
            label="群組類型"
            name="type"
            rules={[{ required: true, message: '請選擇群組類型' }]}
          >
            <Radio.Group>
              <Radio value="circuit">
                <CircuitTag />
              </Radio>
              <Radio value="view">
                <ViewTag />
              </Radio>
            </Radio.Group>
          </Item>
          {isCircuitGroup && (
            <Item
              label="最大同時充電數"
              name="maxChargingNumber"
              rules={[{ required: true, message: '請輸入最大同時充電數' }]}
            >
              <InputNumber
                min={1}
                placeholder="請先選擇群組類型"
                style={{
                  width: '100%',
                }}
              />
            </Item>
          )}
          <Item label="群組成員" name="children">
            <Select mode="multiple" value={form.getFieldValue('children')}>
              {pileData.map((pile) => (
                <Option key={pile._id} value={pile._id}>
                  {pile.chargingPileID}
                </Option>
              ))}
            </Select>
          </Item>
        </Form>
      </Modal>
    </Box>
  );
};

export default ChargingPileGroupManagement;
